// Iter 82.8 Sprint 2 / Phase E3.0 — Salary Structures Master.
// List + create + amend (with side-by-side comparison) + detail view
// with operator-specified preview breakdown (Monthly · Annual · Gross ·
// Employer Contributions · Total CTC).
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Plus, MagnifyingGlass, Eye, PencilSimple,
    Files, TrashSimple, ClockCounterClockwise,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { formatDate } from "../../lib/format";

function todayIso() { return new Date().toISOString().slice(0, 10); }

const NATURE_TONE = {
    earning: "bg-emerald-100 text-emerald-800",
    deduction: "bg-rose-100 text-rose-800",
    employer_contribution: "bg-violet-100 text-violet-800",
    reimbursement: "bg-sky-100 text-sky-800",
};

export default function PayrollStructures() {
    const { can } = useAuth();
    const canEdit = can("payroll:admin");
    const [items, setItems] = useState([]);
    const [components, setComponents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [openCreate, setOpenCreate] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ code: "", name: "", description: "", employee_band: "General", effective_from: todayIso(), components: [], is_active: true });
    const [includeArchived, setIncludeArchived] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const [list, comp] = await Promise.all([
                api.get("/payroll/structure/list", { params: { include_archived: includeArchived } }),
                api.get("/payroll/component/list", { params: { is_active: true } }),
            ]);
            setItems(list.data.items);
            setComponents(comp.data.items);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load structures"));
        } finally {
            setLoading(false);
        }
    }, [includeArchived]);

    useEffect(() => { reload(); }, [reload]);

    const filteredItems = items.filter((i) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (i.code || "").toLowerCase().includes(q) || (i.name || "").toLowerCase().includes(q);
    });

    const openNew = () => {
        setEditing(null);
        setForm({
            code: "", name: "", description: "", employee_band: "General",
            effective_from: todayIso(), is_active: true,
            components: [{ component_code: "BASIC", value_or_formula: "0.50 * GROSS", sequence_no: 10 }],
        });
        setOpenCreate(true);
    };
    const openEdit = (s) => {
        if (s.is_in_use) {
            toast.error("Structure in use — use Amend instead");
            return;
        }
        setEditing(s);
        setForm({
            code: s.code, name: s.name, description: s.description || "",
            employee_band: s.employee_band, effective_from: s.effective_from,
            is_active: s.is_active,
            components: s.components.map((c) => ({
                component_code: c.component_code, value_or_formula: c.value_or_formula,
                monthly_cap: c.monthly_cap, annual_cap: c.annual_cap,
                sequence_no: c.sequence_no, is_optional: c.is_optional,
            })),
        });
        setOpenCreate(true);
    };
    const addRow = () => setForm({ ...form, components: [...form.components, { component_code: "", value_or_formula: "0", sequence_no: 100 }] });
    const updateRow = (idx, patch) => {
        const next = [...form.components];
        next[idx] = { ...next[idx], ...patch };
        setForm({ ...form, components: next });
    };
    const removeRow = (idx) => setForm({ ...form, components: form.components.filter((_, i) => i !== idx) });
    const save = async () => {
        try {
            const payload = {
                ...form,
                components: form.components.filter((c) => c.component_code),
            };
            if (editing) {
                await api.patch(`/payroll/structure/${editing.id}`, payload);
                toast.success(`Updated ${form.code}`);
            } else {
                await api.post("/payroll/structure", payload);
                toast.success(`Created ${form.code}`);
            }
            setOpenCreate(false);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
        }
    };
    const remove = async (s) => {
        if (!window.confirm(`Delete structure ${s.code} v${s.version_no}?`)) return;
        try {
            await api.delete(`/payroll/structure/${s.id}`);
            toast.success("Deleted");
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Delete failed"));
        }
    };

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1500px]" data-testid="payroll-struct-page">
            <PageHeader
                eyebrow="Payroll Masters"
                title="Salary Structures"
                description={`${items.length} structure${items.length === 1 ? "" : "s"} · effective-dated and versioned`}
                actions={(
                    canEdit ? (
                        <Button onClick={openNew} size="sm" data-testid="struct-new-btn">
                            <Plus size={14} className="mr-1" /> New Structure
                        </Button>
                    ) : null
                )}
            />
            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex gap-3 flex-wrap items-end">
                    <div className="flex-1 min-w-[260px]">
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">Search</Label>
                        <div className="relative">
                            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)}
                                   placeholder="Code or name…" data-testid="struct-search" />
                        </div>
                    </div>
                    <label className="flex items-center gap-2 text-xs">
                        <input type="checkbox" checked={includeArchived}
                               onChange={(e) => setIncludeArchived(e.target.checked)}
                               data-testid="struct-archived-toggle" />
                        Include archived
                    </label>
                </div>
            </section>
            <section className="mt-4 bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="struct-table">
                {loading ? (
                    <div className="text-center py-12 text-sm text-slate-400">Loading…</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Code</th>
                                <th className="text-left px-3 py-2 font-medium">Name</th>
                                <th className="text-left px-3 py-2 font-medium">Band</th>
                                <th className="text-center px-3 py-2 font-medium">v</th>
                                <th className="text-left px-3 py-2 font-medium">Effective</th>
                                <th className="text-right px-3 py-2 font-medium">Components</th>
                                <th className="text-left px-3 py-2 font-medium">Status</th>
                                <th className="text-left px-3 py-2 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredItems.map((s) => (
                                <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`struct-row-${s.code}-${s.version_no}`}>
                                    <td className="px-3 py-2 font-mono font-semibold text-slate-800">{s.code}</td>
                                    <td className="px-3 py-2">{s.name}</td>
                                    <td className="px-3 py-2 text-xs">{s.employee_band}</td>
                                    <td className="px-3 py-2 text-center"><Badge className="bg-slate-100 text-slate-700 text-[10px]">v{s.version_no}</Badge></td>
                                    <td className="px-3 py-2 text-xs">{formatDate(s.effective_from)}{s.effective_to ? ` → ${formatDate(s.effective_to)}` : ""}</td>
                                    <td className="px-3 py-2 text-right">{(s.components || []).length}</td>
                                    <td className="px-3 py-2">
                                        {s.is_archived
                                            ? <Badge className="bg-zinc-100 text-zinc-700 text-[10px]">Archived</Badge>
                                            : !s.effective_to
                                                ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Active</Badge>
                                                : <Badge className="bg-amber-100 text-amber-700 text-[10px]">Ended</Badge>}
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="flex gap-1 items-center">
                                            {/* Iter 111.1 UAT · Obs 4 — View action:
                                                higher-contrast Eye icon + visible
                                                "View" label + adequate click target
                                                (min-w 64px, h-8) so operators
                                                immediately recognise it as the
                                                open-detail action. */}
                                            <Link to={`/admin/payroll/structures/${s.id}`}>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-8 min-w-[64px] px-2.5 gap-1.5 border-indigo-300 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800"
                                                    aria-label={`View structure ${s.code} version ${s.version_no}`}
                                                    title="View structure"
                                                    data-testid={`struct-open-${s.code}-${s.version_no}`}
                                                >
                                                    <Eye size={14} weight="duotone" />
                                                    <span className="text-xs font-medium">View</span>
                                                </Button>
                                            </Link>
                                            {!s.is_in_use && !s.effective_to && canEdit && (
                                                <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`struct-edit-${s.code}-${s.version_no}`}>
                                                    <PencilSimple size={12} />
                                                </Button>
                                            )}
                                            {!s.is_in_use && canEdit && (
                                                <Button variant="ghost" size="sm" onClick={() => remove(s)} data-testid={`struct-del-${s.code}-${s.version_no}`}>
                                                    <TrashSimple size={12} className="text-rose-600" />
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {filteredItems.length === 0 && (
                                <tr><td colSpan={8} className="text-center py-12 text-sm text-slate-400">No structures yet — click <strong>New Structure</strong> to begin.</td></tr>
                            )}
                        </tbody>
                    </table>
                )}
            </section>

            <Dialog open={openCreate} onOpenChange={setOpenCreate}>
                <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="struct-dialog">
                    <DialogHeader>
                        <DialogTitle>{editing ? `Edit ${editing.code} v${editing.version_no}` : "New Salary Structure"}</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Code" testid="struct-form-code">
                            <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                                   disabled={!!editing} data-testid="struct-form-code-input" />
                        </Field>
                        <Field label="Name" testid="struct-form-name">
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                        </Field>
                        <Field label="Employee Band" testid="struct-form-band">
                            <Input value={form.employee_band} onChange={(e) => setForm({ ...form, employee_band: e.target.value })}
                                   placeholder="Junior / Mid / Senior / Leadership" />
                        </Field>
                        <Field label="Effective From" testid="struct-form-from">
                            <Input type="date" value={form.effective_from}
                                   onChange={(e) => setForm({ ...form, effective_from: e.target.value })} />
                        </Field>
                        <div className="sm:col-span-2">
                            <Field label="Description" testid="struct-form-desc">
                                <textarea className="w-full border rounded-md text-sm p-2" rows={2}
                                          value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                            </Field>
                        </div>
                    </div>
                    <div className="mt-4">
                        <div className="flex items-center justify-between mb-2">
                            <Label className="text-[10px] uppercase text-slate-500 font-semibold">Components ({form.components.length})</Label>
                            <Button variant="outline" size="sm" onClick={addRow} data-testid="struct-row-add">
                                <Plus size={12} className="mr-1" /> Add Component
                            </Button>
                        </div>
                        <div className="space-y-2" data-testid="struct-row-list">
                            {form.components.map((row, idx) => (
                                <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-slate-50 rounded p-2" data-testid={`struct-row-${idx}`}>
                                    <select className="col-span-3 border rounded-md text-xs h-9 px-2"
                                            value={row.component_code} onChange={(e) => updateRow(idx, { component_code: e.target.value })}
                                            data-testid={`struct-row-${idx}-comp`}>
                                        <option value="">— Component —</option>
                                        {components.map((c) => <option key={c.id} value={c.code}>{c.code} — {c.name}</option>)}
                                    </select>
                                    <Input className="col-span-5 font-mono text-xs" value={row.value_or_formula}
                                           onChange={(e) => updateRow(idx, { value_or_formula: e.target.value })}
                                           placeholder="Value or formula" data-testid={`struct-row-${idx}-formula`} />
                                    <Input type="number" className="col-span-1" placeholder="Seq"
                                           value={row.sequence_no || ""}
                                           onChange={(e) => updateRow(idx, { sequence_no: Number(e.target.value) })} />
                                    <Input type="number" className="col-span-1" placeholder="M Cap"
                                           value={row.monthly_cap || ""}
                                           onChange={(e) => updateRow(idx, { monthly_cap: e.target.value === "" ? null : Number(e.target.value) })} />
                                    <Input type="number" className="col-span-1" placeholder="A Cap"
                                           value={row.annual_cap || ""}
                                           onChange={(e) => updateRow(idx, { annual_cap: e.target.value === "" ? null : Number(e.target.value) })} />
                                    <Button variant="ghost" size="sm" className="col-span-1" onClick={() => removeRow(idx)} data-testid={`struct-row-${idx}-del`}>
                                        <TrashSimple size={12} className="text-rose-600" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </div>
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => setOpenCreate(false)} data-testid="struct-form-cancel">Cancel</Button>
                        <Button onClick={save} data-testid="struct-form-save">{editing ? "Update" : "Create"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Field({ label, children, testid }) {
    return (
        <div data-testid={testid}>
            <Label className="text-[10px] uppercase text-slate-500 font-semibold">{label}</Label>
            <div className="mt-1">{children}</div>
        </div>
    );
}
